import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import './FranchiseDashboard.css'
import { useAuth } from '../context/AuthContext'
import franchiseService from '../services/franchiseService'
import { formatTime } from '../utils/dateFormatter'

const FranchiseDashboard = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [liveOrders, setLiveOrders] = useState([])
    const [newOrderNotification, setNewOrderNotification] = useState(false)
    const prevCountRef = React.useRef(0)
    const prevOrderIdsRef = React.useRef(new Set()) // Track order IDs to detect new orders

    React.useEffect(() => {
        let interval = null
        const POLL_INTERVAL = 5000 // 5 seconds - good balance between real-time and server load

        const fetchLiveOrders = async () => {
            // Don't poll if tab is hidden (saves resources)
            if (document.hidden) {
                return
            }

            try {
                const response = await franchiseService.getLiveOrders()
                if (response.success) {
                    const newOrders = response.data || []
                    const currentOrderIds = new Set(newOrders.map(order => order.id))

                    // Check for new orders (orders that weren't in previous set)
                    const hasNewOrders = newOrders.some(order => !prevOrderIdsRef.current.has(order.id))

                    // Play sound and show notification if there are new orders
                    if (hasNewOrders && newOrders.length > 0) {
                        // Show visual notification
                        setNewOrderNotification(true)
                        setTimeout(() => setNewOrderNotification(false), 3000)

                        // Play sound
                        try {
                            const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3')
                            audio.volume = 0.8 // Set volume
                            const playPromise = audio.play()
                            if (playPromise !== undefined) {
                                playPromise
                                    .then(() => {
                                        console.log('🔔 New order notification sound played')
                                    })
                                    .catch(error => {
                                        console.log("Audio play failed (browser autoplay policy):", error)
                                        // Sound blocked by browser, but visual notification will show
                                    })
                            }
                        } catch (audioError) {
                            console.log("Audio play error:", audioError)
                        }
                    }

                    setLiveOrders(newOrders)
                    prevCountRef.current = newOrders.length
                    prevOrderIdsRef.current = currentOrderIds
                }
            } catch (error) {
                console.error("Error polling live orders:", error)
            }
        }

        // Initial fetch
        fetchLiveOrders()

        // Start polling with fixed interval
        interval = setInterval(fetchLiveOrders, POLL_INTERVAL)

        // Pause polling when tab is hidden, resume when visible
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Tab hidden - stop polling to save resources
                if (interval) {
                    clearInterval(interval)
                    interval = null
                }
            } else {
                // Tab visible - resume polling
                if (!interval) {
                    fetchLiveOrders() // Immediate fetch
                    interval = setInterval(fetchLiveOrders, POLL_INTERVAL)
                }
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            if (interval) clearInterval(interval)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [])

    const handleMarkAsDone = async (orderId) => {
        try {
            // Mark order as done (status: ready) - manual kitchen notification
            const response = await franchiseService.updateOrderStatus(orderId, 'ready')
            if (response.success) {
                // Immediately remove from live orders list (optimistic update)
                setLiveOrders(prev => prev.filter(order => order.id !== orderId))
            } else {
                // If failed, refresh to get correct state
                const ordersResponse = await franchiseService.getLiveOrders()
                if (ordersResponse.success) {
                    setLiveOrders(ordersResponse.data)
                }
            }
        } catch (error) {
            console.error('Error marking order as done:', error)
            alert(error.message || 'Failed to mark order as done')
            // Refresh on error to get correct state
            const ordersResponse = await franchiseService.getLiveOrders()
            if (ordersResponse.success) {
                setLiveOrders(ordersResponse.data)
            }
        }
    }


    return (
        <div className="franchise-dashboard-container">
            <div className="franchise-header">
                <div className="header-content">
                    {user?.logo_url ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <img
                                src={user.logo_url}
                                alt="Franchise Logo"
                                style={{ width: '50px', height: '50px', borderRadius: '12px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.2)' }}
                            />
                            <div>
                                <h1>Franchise Dashboard</h1>
                                <p>Admin: {user?.name}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <h1>Franchise Dashboard</h1>
                            <p>Admin: {user?.name}</p>
                        </>
                    )}
                </div>
                <div className="header-actions">
                    <Link to="/franchise/menu" className="btn-action">
                        Manage Menu
                    </Link>
                    <Link to="/franchise/tables" className="btn-action">
                        Manage Tables
                    </Link>
                </div>
            </div>

            <div className="dashboard-metrics">
                <div className="metric-card">
                    <h3>Active Orders</h3>
                    <div className="metric-value">{liveOrders.length}</div>
                    <span className="metric-trend positive">Live updates</span>
                </div>
                {/* ... other metrics ... */}
            </div>

            <div className="dashboard-content">
                <div className="section-title">
                    <h2>Live Orders</h2>
                    {liveOrders.length > 0 && <span className="pulse-dot"></span>}
                    {newOrderNotification && (
                        <span style={{
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            color: 'white',
                            padding: '0.5rem 1rem',
                            borderRadius: '0.5rem',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            marginLeft: '1rem',
                            animation: 'pulse 1s infinite'
                        }}>
                            New Order!
                        </span>
                    )}
                </div>
                <div className="live-orders-grid">
                    {liveOrders.length === 0 ? (
                        <div className="empty-orders">
                            <p>No active orders</p>
                        </div>
                    ) : (
                        liveOrders.map(order => (
                            <div key={order.id} className="order-card">
                                <div className="order-header">
                                    <span className="table-badge">
                                        {order.table_number ? `Table ${order.table_number}` : (order.tag_type || 'Takeaway')}
                                    </span>
                                    <span className="time-badge">{formatTime(order.created_at)}</span>
                                </div>
                                <div className="order-info">
                                    <p><strong>Order #{order.id}</strong></p>
                                    {order.items && order.items.length > 0 ? (
                                        <div style={{ marginTop: '0.75rem', fontSize: '0.95rem' }}>
                                            {order.items.map((item, idx) => (
                                                <p key={idx} style={{ margin: '0.4rem 0', color: '#e5e7eb', lineHeight: '1.5' }}>
                                                    • {item.name} × {item.quantity}
                                                </p>
                                            ))}
                                        </div>
                                    ) : (
                                        <p style={{ fontSize: '0.85rem', color: '#aaa', fontStyle: 'italic', marginTop: '0.5rem' }}>No items</p>
                                    )}
                                    <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                        <p style={{ fontSize: '1.1rem', fontWeight: '600', color: '#22c55e' }}>
                                            Total: ₹{parseFloat(order.amount).toFixed(2)}
                                        </p>
                                    </div>
                                </div>
                                <div className="order-footer">
                                    <button
                                        className="btn-primary"
                                        onClick={() => handleMarkAsDone(order.id)}
                                        style={{ width: '100%' }}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    )
}

export default FranchiseDashboard
